import {
    isInTrainingMode,
    trainingDayNumber,
    trainingProgressPercent,
} from '@apps/web/lib/training_mode';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export type TrainingModeState = {
    /**
     * False while the organization is still loading.
     *
     * Callers gating a *rule* (rather than just hiding decoration) must wait
     * for this. `isInTrainingMode(undefined)` is `false`, so without a
     * readiness signal "still loading" and "not a training org" are the same
     * value — and a rule like "thumbs-down requires a note" would silently not
     * apply for the first render pass.
     */
    readonly isReady: boolean;
    /**
     * True when the organization fetch failed on a cold load, with no
     * cached organization data to fall back on. A background revalidation
     * failure that still has cached data does not set this — `isReady`
     * stays true and callers keep rendering from the stale data instead of
     * blanking the page.
     */
    readonly error: boolean;
    /** Retries the underlying organization fetch. */
    readonly retry: () => void;
    readonly isInTrainingMode: boolean;
    /** 1-based day within the window; null unless training mode is active. */
    readonly trainingDay: number | null;
    /** 0-100, clamped. Zero unless training mode is active. */
    readonly progressPercent: number;
};

/**
 * Single source of truth for "is this organization in training mode, and how
 * far along is it" — previously re-derived at seven call sites, each repeating
 * the org fetch, the predicate, the day math, and the percent formula.
 *
 * Takes an explicit `organizationId` rather than reading the current org
 * implicitly, because some callers (chat, conversation panes) are scoped to an
 * organization passed down as a prop, not the viewer's own.
 */
export function useTrainingMode({
    organizationId,
    reason,
}: {
    readonly organizationId: string | null;
    readonly reason: string;
}): TrainingModeState {
    const {
        data: organization,
        isLoading,
        error: fetchError,
        mutate,
    } = useOrganization(organizationId, { reason });

    const isReady = !isLoading && organization !== undefined;
    const error = fetchError !== undefined && organization === undefined;
    const retry = () => void mutate();
    const active = isInTrainingMode(organization);

    if (!active || !organization?.training_mode_started_at) {
        return {
            isReady,
            error,
            retry,
            isInTrainingMode: false,
            trainingDay: null,
            progressPercent: 0,
        };
    }

    const trainingDay = trainingDayNumber(
        organization.training_mode_started_at,
        organization.timezone,
    );
    return {
        isReady,
        error,
        retry,
        isInTrainingMode: true,
        trainingDay,
        progressPercent: trainingProgressPercent(trainingDay),
    };
}
